import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { useToast } from '../../../hooks/useToast';

interface UserProfile {
  uid: string;
  name: string;
  organization: string;
  email: string;
  phoneNumber: string;
  createdAt: string;
  updatedAt: string;
  role: string;
  status: string;
  lastLoginAt: string;
  userLevel: number;
}

const UserEditPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser, userLevel: currentUserLevel, permissions, loading: authLoading } = useAdminAuth();
  const { showToast } = useToast();

  const [user, setUser] = useState<UserProfile | null>(null);
  const [originalUser, setOriginalUser] = useState<UserProfile | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // 유틸리티 함수들
  const getUserLevelText = useCallback((level: number): string => {
    switch (level) {
      case 1: return '최고 관리자';
      case 2: return '일반 관리자';
      case 3: return '공사 관계자';
      case 4: return '센터 관계자';
      case 5: return '입주업체';
      case 6: return '입주협력업체';
      case 7: return '공공기관';
      case 8: return '항만근로자';
      case 9: return '일반 사용자';
      default: return '알 수 없음';
    }
  }, []);

  const getStatusText = useCallback((status: string): string => {
    switch (status) {
      case 'active': return '활성';
      case 'inactive': return '비활성';
      case 'suspended': return '일시 정지';
      case 'blocked': return '차단됨';
      default: return '알 수 없음';
    }
  }, []);

  // 권한 체크 - 인증이 완료된 후에만 확인
  const hasPermission = useMemo(() => {
    if (authLoading) return null; // 인증 중에는 null 반환
    if (!currentUser || !currentUserLevel) return false;
    // 최고관리자이거나 사용자 관리 권한이 있는 경우
    return currentUserLevel === 1 || permissions?.users;
  }, [currentUser, currentUserLevel, permissions, authLoading]);

  // 페이지 진입 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchUserData = useCallback(async () => {
    if (!userId) return;

    try {
      setDataLoading(true);
      setError(null);

      const userDoc = await getDoc(doc(db, 'user_profiles', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data() as UserProfile;
        setUser(userData);
        setOriginalUser(userData); // 원본 데이터 저장
      } else {
        setError('사용자를 찾을 수 없습니다.');
      }
    } catch (err) {
      console.error('사용자 정보 조회 중 오류:', err);
      setError(err instanceof Error ? err.message : '사용자 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setDataLoading(false);
    }
  }, [userId]);

  // 권한 확인 및 데이터 로딩 제어
  useEffect(() => {
    if (authLoading) return; // 인증 중에는 아무것도 하지 않음

    if (hasPermission === false) {
      setError('사용자 정보를 수정할 권한이 없습니다.');
      return;
    }

    if (hasPermission === true) {
      fetchUserData();
    }
  }, [hasPermission, authLoading, fetchUserData]);

  // 폼 검증
  const validateForm = useCallback(() => {
    if (!user || !originalUser || !currentUserLevel) return false;

    // 최고관리자 권한으로 변경하려는 경우
    if (user.userLevel === 1) {
      showToast('최고관리자 권한으로 변경할 수 없습니다.', { type: 'warning' });
      return false;
    }

    // 관리자 권한 변경 검증
    if (user.userLevel === 2 && currentUserLevel !== 1) {
      showToast('관리자 권한은 최고관리자만 변경할 수 있습니다.', { type: 'warning' });
      return false;
    }

    // 최고관리자의 계정 상태 변경 검증
    if (originalUser.userLevel === 1 && currentUserLevel !== 1 && originalUser.status !== user.status) {
      showToast('최고관리자의 계정 상태는 다른 최고관리자만 변경할 수 있습니다.', { type: 'warning' });
      return false;
    }

    // 필수 필드 검증
    if (!user.name.trim()) {
      showToast('이름을 입력해주세요.', { type: 'warning' });
      return false;
    }

    if (!user.email.trim()) {
      showToast('이메일을 입력해주세요.', { type: 'warning' });
      return false;
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(user.email)) {
      showToast('올바른 이메일 형식을 입력해주세요.', { type: 'warning' });
      return false;
    }

    return true;
  }, [user, originalUser, currentUserLevel, showToast]);

  // 변경사항 감지
  const hasChanges = useMemo(() => {
    if (!user || !originalUser) return false;
    return (
      user.name !== originalUser.name ||
      user.organization !== originalUser.organization ||
      user.email !== originalUser.email ||
      user.phoneNumber !== originalUser.phoneNumber ||
      user.status !== originalUser.status ||
      user.userLevel !== originalUser.userLevel
    );
  }, [user, originalUser]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !userId || !hasPermission) return;

    // 변경사항이 없는 경우
    if (!hasChanges) {
      showToast('변경된 내용이 없습니다.', { type: 'info' });
      return;
    }

    // 폼 검증
    if (!validateForm()) return;

    setSaving(true);
    setError(null);

    try {
      // 변경된 필드만 업데이트
      const updateData: Partial<UserProfile> = {};

      if (user.name !== originalUser?.name) updateData.name = user.name;
      if (user.organization !== originalUser?.organization) updateData.organization = user.organization;
      if (user.email !== originalUser?.email) updateData.email = user.email;
      if (user.phoneNumber !== originalUser?.phoneNumber) updateData.phoneNumber = user.phoneNumber;
      if (user.status !== originalUser?.status) updateData.status = user.status;
      if (user.userLevel !== originalUser?.userLevel) updateData.userLevel = user.userLevel;

      await updateDoc(doc(db, 'user_profiles', userId), {
        ...updateData,
        updatedAt: serverTimestamp()
      });

      // 권한 동기화는 프로필 변경을 감지하는 onUserProfileAdminClaimsWrite 트리거에서 처리합니다.

      showToast('사용자 정보가 성공적으로 수정되었습니다.', { type: 'success' });
      navigate(`/admin/users/${userId}`);
    } catch (err) {
      console.error('사용자 정보 수정 중 오류:', err);
      const errorMessage = err instanceof Error ? err.message : '사용자 정보 수정 중 오류가 발생했습니다.';
      setError(errorMessage);
      showToast(errorMessage, { type: 'error' });
    } finally {
      setSaving(false);
    }
  }, [user, userId, hasPermission, hasChanges, validateForm, originalUser, navigate, showToast]);

  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (!user) return;

    const { name, value } = e.target;
    const newValue = name === 'userLevel' ? Number(value) : value;

    setUser(prevUser => ({
      ...prevUser!,
      [name]: newValue
    }));

    // 에러 상태가 있다면 클리어
    if (error) {
      setError(null);
    }
  }, [user, error]);

  // 통합된 로딩 상태 - 인증 중이거나 데이터 로딩 중일 때
  if (authLoading || (hasPermission === true && dataLoading)) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb] mx-auto mb-4"></div>
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
              {authLoading ? '권한을 확인하는 중...' : '사용자 정보를 불러오는 중...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 또는 데이터가 없는 경우 (인증이 완료된 후에만 표시)
  if (!authLoading && (error || (hasPermission === true && !user && !dataLoading))) {
    const isPermissionError = error === '사용자 정보를 수정할 권한이 없습니다.';

    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className={`p-8 border-l-4 rounded-r-sm ${isPermissionError ? 'bg-yellow-50 border-yellow-500' : 'bg-red-50 border-red-500'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] mb-2">
                    {isPermissionError ? '접근 권한 없음' : '오류 발생'}
                  </h2>
                  <p className={`text-lg font-medium font-['Pretendard'] mb-4 ${isPermissionError ? 'text-yellow-700' : 'text-red-600'}`}>
                    {error || '사용자를 찾을 수 없습니다.'}
                  </p>
                  <div className="flex gap-3">
                    {!isPermissionError && (
                      <button
                        onClick={() => fetchUserData()}
                        className="px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-sm hover:bg-red-700 transition-colors"
                      >
                        다시 시도
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/admin/users')}
                      className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-sm hover:bg-gray-50 transition-colors"
                    >
                      목록으로 돌아가기
                    </button>
                  </div>
                </div>
                <svg className={`w-12 h-12 ${isPermissionError ? 'text-yellow-400' : 'text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isPermissionError ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 1112 0v1H3v-1z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  )}
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 권한이 없는 경우 또는 아직 데이터가 로드되지 않은 경우 아무것도 렌더링하지 않음
  if (hasPermission !== true || !user) {
    return null;
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              사용자 정보 수정
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/admin/users/${userId}`)}
                className="px-4 py-2 border border-gray-300 text-[#6b7684] text-base font-medium font-['Pretendard'] rounded-sm hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              {hasChanges && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  변경사항 있음
                </span>
              )}
            </div>
      </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 수정 폼 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              사용자 정보
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
            
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-2 gap-8">
          <div>
                  <label htmlFor="name" className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
              이름
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={user.name}
              onChange={handleChange}
                    className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              required
            />
          </div>

          <div>
                  <label htmlFor="email" className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
              이메일
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={user.email}
              onChange={handleChange}
                    className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              required
            />
          </div>

          <div>
                  <label htmlFor="organization" className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
              소속
            </label>
            <input
              type="text"
              id="organization"
              name="organization"
              value={user.organization}
              onChange={handleChange}
                    className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              required
            />
          </div>

          <div>
                  <label htmlFor="phoneNumber" className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
              전화번호
            </label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={user.phoneNumber}
              onChange={handleChange}
                    className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              required
            />
          </div>

          <div>
                  <label htmlFor="status" className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
              계정 상태
            </label>
            {originalUser?.userLevel === 1 && currentUserLevel !== 1 ? (
                    <div>
                      <div className="w-full h-12 bg-gray-50 rounded-sm outline outline-[0.50px] outline-gray-300 px-4 flex items-center text-[#1e2c49] text-lg font-medium font-['Pretendard']">
                        {getStatusText(user.status)}
                      </div>
                      <p className="mt-2 text-yellow-600 text-sm font-normal font-['Pretendard']">
                        최고관리자의 계정 상태는 다른 최고관리자만 변경할 수 있습니다.
                </p>
              </div>
            ) : (
              <select
                id="status"
                name="status"
                value={user.status}
                onChange={handleChange}
                      className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
              >
                <option value="active">활성</option>
                <option value="inactive">비활성</option>
                <option value="suspended">일시 정지</option>
                <option value="blocked">차단됨</option>
              </select>
            )}
          </div>

          <div>
                  <label htmlFor="userLevel" className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">
              사용자 권한
            </label>
            {originalUser?.userLevel === 1 ? (
                    <div>
                      <div className="w-full h-12 bg-gray-50 rounded-sm outline outline-[0.50px] outline-gray-300 px-4 flex items-center text-[#1e2c49] text-lg font-medium font-['Pretendard']">
                        {getUserLevelText(user.userLevel)}
                      </div>
                      <p className="mt-2 text-yellow-600 text-sm font-normal font-['Pretendard']">
                        최고관리자 권한은 변경할 수 없습니다.
                      </p>
              </div>
            ) : (
              <>
                <select
                  id="userLevel"
                  name="userLevel"
                  value={user.userLevel}
                  onChange={handleChange}
                        className="w-full h-12 bg-white rounded-sm outline outline-[0.50px] outline-gray-300 px-4 text-[#1e2c49] text-lg font-medium font-['Pretendard'] focus:outline-[#155acb] focus:outline-2"
                        disabled={originalUser?.userLevel === 2 && currentUserLevel !== 1}
                >
                  <option value="9">일반 사용자</option>
                  <option value="8">항만근로자</option>
                  <option value="7">공공기관</option>
                  <option value="6">입주협력업체</option>
                  <option value="5">입주업체</option>
                  <option value="4">센터 관계자</option>
                  <option value="3">공사 관계자</option>
                  {currentUserLevel === 1 && <option value="2">일반 관리자</option>}
                </select>
                {originalUser?.userLevel === 2 && currentUserLevel !== 1 && (
                        <p className="mt-2 text-yellow-600 text-sm font-normal font-['Pretendard']">
                          관리자 권한은 최고관리자만 변경할 수 있습니다.
                        </p>
                )}
              </>
            )}
                </div>
          </div>

          <div className="flex justify-between items-center pt-6">
            <div className="text-sm text-[#6b7684]">
              {hasChanges ? (
                <span className="text-blue-600">* 변경사항이 있습니다. 저장하시겠습니까?</span>
              ) : (
                <span>변경사항이 없습니다.</span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate(`/admin/users/${userId}`)}
                className="px-4 py-2 border border-gray-300 text-[#6b7684] text-base font-medium font-['Pretendard'] rounded-sm hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={saving || !hasChanges}
                    className="w-[130px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
              </div>
            </form>
          </div>
        </div>

        {/* 폼 안내 정보 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-6 bg-blue-50 border-l-4 border-blue-500 rounded-r-sm">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-blue-800 text-sm font-medium mb-1">사용자 정보 수정 안내</p>
                <p className="text-blue-700 text-xs">
                  • 최고관리자 권한은 변경할 수 없습니다.<br />
                  • 관리자 권한 변경은 최고관리자만 가능합니다.<br />
                  • 변경사항은 즉시 적용되며, 저장 후 되돌릴 수 없습니다.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default UserEditPage; 
